import { mapHttpError } from '@/lib/mapApiError';
import type {
  StripeSubscriptionPaymentResumeDto,
  SubscriptionPaymentAction,
  WorkspaceSubscriptionStatus,
} from '@/features/billing/types';

/** Codes métier du tunnel de souscription, tels que le back les renvoie. */
export const SUBSCRIPTION_ERROR_CODES = {
  alreadySubscribed: 'HTTP.ERROR.400.WORKSPACE.ALREADY.SUBSCRIBED',
  promoFirstTransactionUsed: 'HTTP.ERROR.400.BILLING.PROMOTION.FIRST_TRANSACTION.ALREADY_USED',
  pendingPriceMismatch: 'HTTP.ERROR.409.BILLING.PENDING_SUBSCRIPTION.PRICE_MISMATCH',
  priceCurrencyMismatch: 'HTTP.ERROR.400.BILLING.PRICE.CURRENCY.MISMATCH',
  requestInProgress: 'HTTP.ERROR.409.STRIPE.REQUEST_IN_PROGRESS',
} as const;

/**
 * Le back place le code métier à deux endroits selon l'exception levée : dans `errors[]` quand il
 * sérialise une ValidationError (le `message` racine vaut alors le code générique de validation),
 * et dans le `message` racine quand il lève un conflit avec une chaîne brute. Lire les deux est
 * indispensable, sinon le conflit de prix retombe sur « Requête invalide ». Une erreur rejetée par
 * un thunk billing a déjà son code dans `apiCode`, son `message` étant remplacé par une traduction.
 */
export function resolveApiErrorCode(error: unknown): string | null {
  if (!error || typeof error !== 'object') return null;
  const { apiCode, details, message } = error as {
    apiCode?: unknown;
    details?: unknown;
    message?: unknown;
  };

  if (typeof apiCode === 'string' && apiCode.startsWith('HTTP.')) return apiCode;

  if (Array.isArray(details)) {
    for (const detail of details) {
      const detailMessage = (detail as { message?: unknown })?.message;
      if (typeof detailMessage === 'string' && detailMessage.startsWith('HTTP.')) {
        return detailMessage;
      }
    }
  }

  return typeof message === 'string' && message.startsWith('HTTP.') ? message : null;
}

/**
 * Une souscription laissée en attente de paiement existe déjà chez Stripe : relancer une création
 * la ferait refuser (le code promo première transaction est déjà consommé par sa facture initiale).
 * On passe donc par la reprise plutôt que par le POST de création.
 */
export function shouldResumePendingPayment(
  status: WorkspaceSubscriptionStatus | null | undefined,
): boolean {
  return status === 'INCOMPLETE' || status === 'PAST_DUE';
}

/**
 * La reprise confirme le paiement de la souscription en attente, dont le forfait est figé chez
 * Stripe. Si le client en sélectionne un autre entre-temps, reprendre lui ferait payer l'ancien :
 * on laisse alors partir la création, que le back refuse avec son conflit de prix.
 * Comparaison sur le nom et la périodicité faute d'identifiant de prix dans la réponse de reprise.
 */
export function isSamePendingPlan(
  pending: { planName: string; interval: string } | null | undefined,
  requested: { nickname: string; interval: string } | null | undefined,
): boolean {
  if (!pending || !requested) return false;
  return (
    pending.planName.trim().toLowerCase() === requested.nickname.trim().toLowerCase() &&
    pending.interval.trim().toLowerCase() === requested.interval.trim().toLowerCase()
  );
}

export type ResumeStep =
  | { kind: 'confirm'; clientSecret: string; subscriptionId: string }
  | { kind: 'hostedInvoice'; url: string }
  | { kind: 'nothingToPay' }
  | { kind: 'create' }
  | { kind: 'unusable' };

/**
 * Traduit la réponse de reprise en geste à poser. Seul `CREATE_NEW_SUBSCRIPTION` autorise une
 * création : une action annoncée sans son secret ni son URL n'est pas jouable, et retomber sur la
 * création reviendrait à envoyer un second paiement alors qu'une souscription est déjà en attente.
 */
export function resolveResumeStep(
  resume: StripeSubscriptionPaymentResumeDto | null | undefined,
): ResumeStep {
  if (!resume) return { kind: 'unusable' };

  const action: SubscriptionPaymentAction = resume.paymentAction;

  if (action === 'CONFIRM_PAYMENT_INTENT') {
    return resume.paymentIntentClientSecret
      ? {
          kind: 'confirm',
          clientSecret: resume.paymentIntentClientSecret,
          subscriptionId: resume.subscriptionId,
        }
      : { kind: 'unusable' };
  }

  if (action === 'PAY_HOSTED_INVOICE') {
    return resume.hostedInvoiceUrl
      ? { kind: 'hostedInvoice', url: resume.hostedInvoiceUrl }
      : { kind: 'unusable' };
  }

  // La souscription existe et n'attend aucun paiement : recréer se ferait refuser, on se contente
  // de resynchroniser l'état de facturation.
  if (action === 'NO_PAYMENT_ACTION') {
    return { kind: 'nothingToPay' };
  }

  return action === 'CREATE_NEW_SUBSCRIPTION' ? { kind: 'create' } : { kind: 'unusable' };
}

/**
 * La reprise est annoncée mais injouable, ou l'API de reprise est tombée. On arrête le tunnel :
 * enchaîner sur une création enverrait un second paiement sur une souscription déjà en attente.
 */
export class SubscriptionResumeUnavailableError extends Error {
  constructor() {
    super('subscription payment resume unavailable');
    this.name = 'SubscriptionResumeUnavailableError';
  }
}

/** Clé i18n du message à afficher pour un échec de tunnel, ou `null` s'il n'y a rien de précis. */
/** Prix d'une autre devise que celle du workspace : le catalogue affiché est périmé, il faut le recharger. */
export function isPriceCurrencyMismatch(error: unknown): boolean {
  return resolveApiErrorCode(error) === SUBSCRIPTION_ERROR_CODES.priceCurrencyMismatch;
}

export function resolveCheckoutErrorKey(error: unknown): string | null {
  if (error instanceof SubscriptionResumeUnavailableError) {
    return 'errors.billing.resumeUnavailable';
  }
  const apiCode = resolveApiErrorCode(error);
  return apiCode ? mapHttpError(apiCode) : null;
}

export type CheckoutOutcome =
  | { kind: 'confirm'; subscriptionId: string; clientSecret: string }
  | { kind: 'hostedInvoice'; url: string }
  | { kind: 'settled' };

/** Geste final attendu du tunnel, ou `null` quand il reste à créer la souscription. */
export function toCheckoutOutcome(step: ResumeStep): CheckoutOutcome | null {
  switch (step.kind) {
    case 'confirm':
      return {
        kind: 'confirm',
        subscriptionId: step.subscriptionId,
        clientSecret: step.clientSecret,
      };
    case 'hostedInvoice':
      return { kind: 'hostedInvoice', url: step.url };
    case 'nothingToPay':
      return { kind: 'settled' };
    default:
      return null;
  }
}
